import { useEffect, useState } from "react";
import { MOVIES, type Movie, type DownloadLink } from "./movies-data";
import { getContentCatalog } from "./content/adapter";
import type { Article } from "./article-types";

export type CustomMovie = Movie & {
  /** @deprecated kept for legacy data read; new items use `article` */
  paragraphs?: string[];
  /** Rich article shown before the comments section */
  article?: Article;
  isCustom?: true;
  addedAt?: number;
};

const KEY = "rawan-library-v1";
type Listener = () => void;
const listeners = new Set<Listener>();

function read(): CustomMovie[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as CustomMovie[]) : [];
  } catch {
    return [];
  }
}
function write(next: CustomMovie[]) {
  localStorage.setItem(KEY, JSON.stringify(next));
  listeners.forEach((l) => l());
}

export function getCustomItems(): CustomMovie[] {
  return read();
}

export function getAllItems(): CustomMovie[] {
  // Order: user-added (LS) → GitHub JSON content → built-in seed.
  // Dedupe by slug so a content entry can override the seed by sharing its slug.
  const seen = new Set<string>();
  const out: CustomMovie[] = [];
  for (const m of [...read(), ...getContentCatalog(), ...MOVIES]) {
    if (seen.has(m.slug)) continue;
    seen.add(m.slug);
    out.push(m as CustomMovie);
  }
  return out;
}

export function getItemBySlug(slug: string): CustomMovie | undefined {
  return getAllItems().find((m) => m.slug === slug);
}

export function addItem(item: Omit<CustomMovie, "addedAt" | "isCustom">): CustomMovie {
  const next: CustomMovie = { ...item, isCustom: true, addedAt: Date.now() };
  // de-dupe by slug
  const without = read().filter((m) => m.slug !== item.slug);
  write([next, ...without]);
  return next;
}

export function updateItem(slug: string, patch: Partial<CustomMovie>): void {
  write(read().map((m) => (m.slug === slug ? { ...m, ...patch } : m)));
}

export function deleteItem(slug: string): void {
  write(read().filter((m) => m.slug !== slug));
}

export function useAllItems(): CustomMovie[] {
  const [, setTick] = useState(0);
  useEffect(() => {
    const l: Listener = () => setTick((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return getAllItems();
}

export function useCustomItems(): CustomMovie[] {
  const [, setTick] = useState(0);
  useEffect(() => {
    const l: Listener = () => setTick((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return getCustomItems();
}

/* ============ Import / Export ============ */

export function exportLibraryJSON(extra?: Record<string, unknown>): string {
  return JSON.stringify(
    {
      version: 1,
      exportedAt: new Date().toISOString(),
      items: read(),
      ...extra,
    },
    null,
    2,
  );
}

export function importLibraryJSON(raw: string): { added: number } {
  const parsed = JSON.parse(raw) as { items?: CustomMovie[] };
  if (!parsed?.items || !Array.isArray(parsed.items)) throw new Error("ملف غير صالح");
  const existing = read();
  const map = new Map(existing.map((m) => [m.slug, m]));
  for (const it of parsed.items) map.set(it.slug, { ...it, isCustom: true });
  write(Array.from(map.values()));
  return { added: parsed.items.length };
}

export type { DownloadLink };
